import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ArrowRight, Info, Check, Sparkles } from "lucide-react";
import { SEO, SITE_URL } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { useContent } from "../lib/useContent";
import { CategoryBadge } from "./Blog";
import { SITE_LOGO } from "../data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, loading, notFound } = useContent(`/blog-posts/${slug}`, [slug]);
  if (loading) return <PageLoader />;
  if (notFound || !post) return <Navigate to="/blog" replace />;

  const next = post.next;
  const nextTo = next.path || `/blog/${next.slug}`;
  const canonical = post.path || `/blog/${post.slug}`;

  let isoDate;
  try {
    isoDate = new Date(post.date).toISOString();
  } catch {
    isoDate = undefined;
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: post.image,
    ...(isoDate ? { datePublished: isoDate, dateModified: isoDate } : {}),
    author: { "@type": "Organization", name: "Travel Club Navigator Group" },
    publisher: {
      "@type": "Organization",
      name: "Travel Club Navigator Group",
      logo: { "@type": "ImageObject", url: SITE_LOGO },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${canonical}`,
    },
    ...(post.keywords ? { keywords: post.keywords.join(", ") } : {}),
    articleSection: post.category,
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.metaDescription || post.excerpt}
        path={canonical}
        image={post.image}
        keywords={post.keywords}
        jsonLd={jsonLd}
        type="article"
        publishedTime={isoDate}
        author={post.author}
      />

      {/* HERO */}
      <section className="relative flex min-h-[56vh] items-end overflow-hidden">
        <img src={post.image} alt={post.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-3xl px-5 pb-14 pt-32 lg:px-8">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All stories
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-5"
          >
            <CategoryBadge category={post.category} sponsored={post.sponsored} />
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-300">
              <span>{post.author}</span>
              <span>·</span>
              <span>{post.date}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" /> {post.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BODY */}
      <article className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        {post.logo && (
          <Reveal>
            <div className="mb-10 flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <img
                src={post.logo}
                alt="Travel Club Navigator Group logo"
                className="h-12 w-auto object-contain"
              />
              <p className="text-sm font-medium text-slate-500">
                Published by the <span className="text-navy">Travel Club Navigator Group</span> editorial team.
              </p>
            </div>
          </Reveal>
        )}

        {post.sponsored && (
          <Reveal>
            <div className="mb-10 flex items-start gap-3 rounded-2xl border border-accent/30 bg-accent/10 p-5">
              <Info className="mt-0.5 h-5 w-5 flex-none text-navy" />
              <p className="text-sm leading-relaxed text-navy">
                <strong>Sponsored content.</strong> This feature is produced in partnership with
                the brand named above. Our editorial standards and honest pros/cons approach still
                apply — transparency is the whole point.
              </p>
            </div>
          </Reveal>
        )}

        <Reveal>
          <p className="font-serif-accent text-2xl leading-relaxed text-navy">
            {post.intro || post.excerpt}
          </p>
        </Reveal>

        {post.graphic && (
          <Reveal delay={0.05}>
            <figure className="mt-10 overflow-hidden rounded-3xl border border-slate-100 bg-white p-2 shadow-sm">
              <img
                src={post.graphic.url}
                alt={post.graphic.alt}
                className="w-full rounded-2xl"
              />
              {post.graphic.caption && (
                <figcaption className="px-4 py-4 text-center text-sm italic text-slate-500">
                  {post.graphic.caption}
                </figcaption>
              )}
            </figure>
          </Reveal>
        )}

        <div className="mt-12 space-y-12">
          {post.sections.map((s, i) => (
            <Reveal key={s.heading} delay={0.04}>
              <section>
                <Overline>{`0${i + 1}`}</Overline>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy">
                  {s.heading}
                </h2>
                {s.body && (
                  <p className="mt-4 text-lg leading-relaxed text-slate-600">{s.body}</p>
                )}

                {s.image && (
                  <img
                    src={s.image.url}
                    alt={s.image.alt}
                    className="mt-6 h-64 w-full rounded-2xl object-cover shadow-sm sm:h-80"
                  />
                )}

                {s.figure && (
                  <figure className="mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
                    <img
                      src={s.figure.url}
                      alt={s.figure.alt}
                      className="w-full rounded-xl"
                    />
                    {s.figure.caption && (
                      <figcaption className="px-4 py-3 text-center text-sm italic text-slate-500">
                        {s.figure.caption}
                      </figcaption>
                    )}
                  </figure>
                )}

                {s.bullets && (
                  <ul className="mt-6 space-y-4">
                    {s.bullets.map((b) => (
                      <li key={b.t} className="flex items-start gap-3">
                        <Check className="mt-1 h-5 w-5 flex-none text-secondary" />
                        <span className="text-lg leading-relaxed text-slate-600">
                          <strong className="text-navy">{b.t}:</strong> {b.d}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {s.table && (
                  <div className="mt-6 overflow-hidden rounded-2xl border border-slate-100 shadow-sm">
                    <table className="w-full border-collapse text-left text-sm">
                      <thead>
                        <tr className="bg-navy text-white">
                          {s.table.columns.map((c) => (
                            <th key={c} className="px-4 py-3 font-display font-semibold">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, ri) => (
                          <tr
                            key={`${row[0] ?? "row"}-${ri}`}
                            className={ri % 2 ? "bg-muted/60" : "bg-white"}
                          >
                            {row.map((cell, ci) => (
                              <td
                                key={`${ri}-${ci}`}
                                className={`px-4 py-3 align-top ${
                                  ci === 0 ? "font-semibold text-navy" : "text-slate-600"
                                }`}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {s.callout && (
                  <div className="mt-6 flex gap-4 rounded-2xl bg-secondary/10 p-6">
                    <Sparkles className="h-6 w-6 flex-none text-secondary" />
                    <p className="text-base leading-relaxed text-navy">{s.callout}</p>
                  </div>
                )}
              </section>
            </Reveal>
          ))}
        </div>

        {post.cta && (
          <Reveal delay={0.05}>
            <div className="mt-14 rounded-3xl bg-navy p-8 text-center sm:p-10">
              <h3 className="font-display text-2xl font-bold text-white">{post.cta.title}</h3>
              {post.cta.text && <p className="mt-2 text-slate-300">{post.cta.text}</p>}
              <Link
                to={post.cta.to}
                data-testid="blog-cta-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground btn-glow transition-transform duration-200 hover:scale-105"
              >
                {post.cta.label} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        )}

        {post.seriesNext && (
          <Reveal delay={0.05}>
            <p className="mt-8 rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-center font-serif-accent text-lg italic text-slate-500">
              {post.seriesNext}
            </p>
          </Reveal>
        )}

        <Reveal delay={0.05}>
          <Link
            to={nextTo}
            data-testid="blog-next-link"
            className="group mt-10 flex items-center justify-between rounded-3xl card-3d p-6"
          >
            <span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Next story
              </span>
              <span className="mt-1 block font-display text-lg font-bold text-navy">
                {next.title}
              </span>
            </span>
            <ArrowRight className="h-6 w-6 text-primary transition-transform duration-200 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </article>
    </>
  );
}
